import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
// Material UI Core Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';

// Material UI Icons
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import RefreshIcon from '@mui/icons-material/Refresh';

function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [exams, setExams] = useState([]);
  const [results, setResults] = useState([]);
  const [proctoringLogs, setProctoringLogs] = useState([]);
  const [viewLogsDialogOpen, setViewLogsDialogOpen] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedExamId, setSelectedExamId] = useState(null);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    totalExams: 0,
    completedExams: 0,
    ongoingExams: 0,
    upcomingExams: 0,
    totalStudents: 0,
    totalTeachers: 0
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [activeSection, setActiveSection] = useState('dashboard');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [examTeacherFilter, setExamTeacherFilter] = useState('');
  const [examSubjectFilter, setExamSubjectFilter] = useState('');
  const [examDateFilter, setExamDateFilter] = useState('');
  const [deleteTeacherDialogOpen, setDeleteTeacherDialogOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState(null);
  const [deleteStudentDialogOpen, setDeleteStudentDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  
  const drawerWidth = 240;
  
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in and has admin role
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!storedUser || !storedUser.username) {
      // Redirect to login if not logged in
      navigate('/');
      return;
    }
    
    if (storedUser.role !== 'admin') {
      // Redirect to appropriate dashboard based on role
      if (storedUser.role === 'teacher') {
        navigate('/teacher-dashboard');
      } else {
        navigate('/proctor');
      }
      return;
    }
    
    setUser(storedUser);
    
    // Fetch initial data
    fetchTeachers(storedUser);
    fetchStudents(storedUser);
    fetchExams(storedUser);
    fetchResults(storedUser);
    fetchProctoringLogs(storedUser);
    
    // Set up interval to refresh data every 30 seconds
    const intervalId = setInterval(() => {
      if (storedUser) {
        fetchTeachers(storedUser);
        fetchStudents(storedUser);
        fetchExams(storedUser);
        fetchResults(storedUser);
        fetchProctoringLogs(storedUser);
      }
    }, 30000);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [navigate]);
  
  // Update dashboard stats whenever data changes
  useEffect(() => {
    const now = new Date().toISOString();
    
    // Calculate stats based on exam status
    const completedExams = exams.filter(exam => exam.status === 'completed').length;
    const ongoingExams = exams.filter(exam => exam.status === 'ongoing').length;
    const upcomingExams = exams.filter(exam => exam.status === 'upcoming').length;
    
    setDashboardStats({
      totalExams: exams.length,
      completedExams,
      ongoingExams,
      upcomingExams,
      totalStudents: students.length,
      totalTeachers: teachers.length
    });
  }, [exams, students, teachers]);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setMobileOpen(false);
  };
  
  const handleOpenDialog = () => {
    // Reset form fields
    setUsername('');
    setPassword('');
    setName('');
    setEmail('');
    setError('');
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Password validation function
  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const handleCreateTeacher = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      setError('Username and password are required');
      showSnackbar('Username and password are required', 'error');
      return;
    }

    // Email validation
    if (email && !validateEmail(email)) {
      setError('Please enter a valid email address');
      showSnackbar('Please enter a valid email address', 'error');
      return;
    }

    // Password validation
    if (!validatePassword(password)) {
      setError('Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one digit, and one special character (@$!%*?&)');
      showSnackbar('Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one digit, and one special character (@$!%*?&)', 'error');
      return;
    }
    
    if (!user || !user.username || !user.password) {
      setError('Admin credentials required. Please log in again.');
      showSnackbar('Admin credentials required. Please log in again.', 'error');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/create-teacher`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
          name: name || username,
          email: email || '',
          admin_username: user.username,
          admin_password: user.password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        showSnackbar('Teacher account created successfully', 'success');
        setUsername('');
        setPassword('');
        setName('');
        setEmail('');
        setDialogOpen(false);
        
        // Refresh teachers list
        fetchTeachers(user);
      } else {
        setError(data.error || 'Failed to create teacher account');
        showSnackbar(data.error || 'Failed to create teacher account', 'error');
      }
    } catch (err) {
      setError('Error creating teacher account: ' + err.message);
      showSnackbar('Error creating teacher account: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeachers = async (user) => {
    try {
      // Create a token by base64 encoding the user object
      // In a real app, you would use a proper JWT token
      const token = btoa(JSON.stringify(user));
      
      const response = await fetch(API_ENDPOINTS.ADMIN_TEACHERS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setTeachers(data.teachers || []);
      } else {
        setError(data.message || data.error || 'Failed to fetch teachers');
        showSnackbar(data.message || data.error || 'Failed to fetch teachers', 'error');
      }
    } catch (err) {
      console.error('Error fetching teachers:', err);
      setError('Error fetching teachers: ' + err.message);
      showSnackbar('Error fetching teachers: ' + err.message, 'error');
    }
  };

  const fetchStudents = async (user) => {
    try {
      // Create a token by base64 encoding the user object
      const token = btoa(JSON.stringify(user));
      
      const response = await fetch(API_ENDPOINTS.ADMIN_STUDENTS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setStudents(data.students || []);
      } else {
        console.error('Error fetching students:', data.error);
        showSnackbar(data.message || data.error || 'Failed to fetch students', 'error');
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      showSnackbar('Error fetching students: ' + err.message, 'error');
    }
  };

  const fetchExams = async (user) => {
    try {
      // Create a token by base64 encoding the user object
      const token = btoa(JSON.stringify(user));
      
      const response = await fetch(API_ENDPOINTS.ADMIN_EXAMS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setExams(data.exams || []);
      } else {
        console.error('Error fetching exams:', data.error);
        showSnackbar(data.message || data.error || 'Failed to fetch exams', 'error');
      }
    } catch (err) {
      console.error('Error fetching exams:', err);
      showSnackbar('Error fetching exams: ' + err.message, 'error');
    }
  };

  const fetchResults = async (user) => {
    try {
      // Create a token by base64 encoding the user object
      const token = btoa(JSON.stringify(user));
      
      const response = await fetch(API_ENDPOINTS.ADMIN_RESULTS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setResults(data.results || []);
      } else {
        console.error('Error fetching results:', data.error);
        showSnackbar(data.message || data.error || 'Failed to fetch results', 'error');
      }
    } catch (err) {
      console.error('Error fetching results:', err);
      showSnackbar('Error fetching results: ' + err.message, 'error');
    }
  };

  const fetchProctoringLogs = async (user) => {
    try {
      // Create a token by base64 encoding the user object
      const token = btoa(JSON.stringify(user));
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/proctoring-logs`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setProctoringLogs(data.logs || []);
      } else {
        console.error('Error fetching proctoring logs:', data.error);
        showSnackbar(data.message || data.error || 'Failed to fetch proctoring logs', 'error');
      }
    } catch (err) {
      console.error('Error fetching proctoring logs:', err);
      showSnackbar('Error fetching proctoring logs: ' + err.message, 'error');
    }
  };

  const handleViewLogs = (examId, studentId) => {
    setSelectedExamId(examId);
    setSelectedStudentId(studentId);
    setViewLogsDialogOpen(true);
  };
  
  // Function to get warning title based on event type and description
  const getWarningTitle = (eventType, description) => {
    switch (eventType) {
      case 'AUDIO':
        return 'Noise Detected';
      case 'PERSON':
        return description.includes('multiple') ? 'Multiple Persons Detected' : 'Person Missing';
      case 'GAZE':
        return 'Looking Away';
      case 'PHONE':
        return 'Phone Detected';
      case 'MOUTH':
        return 'Talking Detected';
      case 'HEAD':
        return 'Head Position Changed';
      case 'SPOOF':
        return 'Spoofing Attempt Detected';
      case 'FOCUS':
        return 'Tab Change Detected';
      case 'SESSION':
        return 'Session Event';
      default:
        return '';
    }
  };
  
  const handleOpenWarningDialog = (warning) => {
    setSelectedWarning(warning);
    setWarningDialogOpen(true);
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Manual refresh function for admin dashboard
  const handleManualRefresh = async () => {
    if (!user) {
      showSnackbar('User not authenticated', 'error');
      return;
    }

    try {
      setLoading(true);
      setError(''); // Clear any existing errors

      console.log('Starting manual refresh...');

      // Fetch all data sequentially
      await fetchTeachers(user);
      await fetchStudents(user);
      await fetchExams(user);
      await fetchResults(user);
      await fetchProctoringLogs(user);

      console.log('Manual refresh completed successfully');
      showSnackbar('Dashboard refreshed successfully', 'success');

    } catch (error) {
      console.error('Error during manual refresh:', error);
      setError('Failed to refresh dashboard. Please try again.');
      showSnackbar('Failed to refresh dashboard. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeacher = async () => {
    if (!teacherToDelete) return;
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/delete-teacher`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: teacherToDelete.username,
          admin_username: user.username,
          admin_password: user.password
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showSnackbar('Teacher deleted successfully', 'success');
        setDeleteTeacherDialogOpen(false);
        setTeacherToDelete(null);
        fetchTeachers(user);
      } else {
        showSnackbar(data.error || 'Failed to delete teacher', 'error');
      }
    } catch (err) {
      showSnackbar('Error deleting teacher: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = async () => {
    if (!studentToDelete) return;
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/delete-student`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: studentToDelete.username,
          admin_username: user.username,
          admin_password: user.password
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showSnackbar('Student deleted successfully', 'success');
        setDeleteStudentDialogOpen(false);
        setStudentToDelete(null);
        fetchStudents(user);
      } else {
        showSnackbar(data.error || 'Failed to delete student', 'error');
      }
    } catch (err) {
      showSnackbar('Error deleting student: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  // Sidebar content
  const drawer = (
    <Box sx={{ bgcolor: '#f5f5f5', height: '100%' }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0277bd' }}>
          ViGiLANT
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Online Exam Management
        </Typography>
      </Box>
      
      <List component="nav" sx={{ pt: 1 }}>
        <ListItemButton 
          selected={activeSection === 'dashboard'}
          onClick={() => handleSectionChange('dashboard')}
        >
          <ListItemIcon>
            <DashboardIcon color={activeSection === 'dashboard' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>
        
        <ListItemButton 
          selected={activeSection === 'teachers'}
          onClick={() => handleSectionChange('teachers')}
        >
          <ListItemIcon>
            <PersonIcon color={activeSection === 'teachers' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Teachers" />
        </ListItemButton>
        
        <ListItemButton 
          selected={activeSection === 'students'}
          onClick={() => handleSectionChange('students')}
        >
          <ListItemIcon>
            <SchoolIcon color={activeSection === 'students' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Students" />
        </ListItemButton>
        
        <ListItemButton 
          selected={activeSection === 'exams'}
          onClick={() => handleSectionChange('exams')}
        >
          <ListItemIcon>
            <AssignmentIcon color={activeSection === 'exams' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Exams" />
        </ListItemButton>
        
        <ListItemButton 
          selected={activeSection === 'results'}
          onClick={() => handleSectionChange('results')}
        >
          <ListItemIcon>
            <AssessmentIcon color={activeSection === 'results' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Results & Proctoring" />
        </ListItemButton>
        
        <Divider sx={{ my: 2 }} />
        
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </List>
    </Box>
  );
  
  return (
    <>
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <CssBaseline />
        
        {/* App Bar */}
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
            bgcolor: '#0277bd'
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              {activeSection === 'dashboard' && 'Dashboard'}
              {activeSection === 'teachers' && 'Teacher Management'}
              {activeSection === 'students' && 'Student Management'}
              {activeSection === 'exams' && 'Exam Management'}
              {activeSection === 'results' && 'Results & Proctoring'}
            </Typography>

            {/* Manual Refresh Button */}
            <Tooltip title="Refresh Dashboard">
              <IconButton
                color="inherit"
                onClick={handleManualRefresh}
                disabled={loading}
                sx={{ mr: 1 }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            {/* Logout Button */}
            <Tooltip title="Logout">
              <IconButton
                color="inherit"
                onClick={handleLogout}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        
        {/* Sidebar */}
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        >
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
          
          {/* Desktop drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        
        {/* Main content */}
        <Box
          component="main"
          sx={{ 
            flexGrow: 1, 
            p: 3, 
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            mt: { xs: 7, sm: 8 },
            bgcolor: '#f9f9f9',
            height: '100%',
            overflow: 'auto'
          }}
        >
          {/* Error message if any */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              icon={<ErrorIcon fontSize="inherit" />}
            >
              {error}
            </Alert>
          )}
          
          {/* Dashboard Section */}
          {activeSection === 'dashboard' && (
            <>
              {/* Exam Statistics Section */}
              <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Exam Stats
                </Typography>
                
                <Grid container spacing={2}>
                  {/* Total Exams */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#2196f3', color: 'white', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">Total Exams</Typography>
                          <DeleteIcon />
                        </Box>
                        <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>{dashboardStats.totalExams}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Exams Completed */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#4caf50', color: 'white', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">Exams Completed</Typography>
                          <CheckCircleIcon />
                        </Box>
                        <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>{dashboardStats.completedExams}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Ongoing Exams */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#ff9800', color: 'white', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">On-going Exams</Typography>
                          <AccessTimeIcon />
                        </Box>
                        <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>{dashboardStats.ongoingExams}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Upcoming Exams */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#f44336', color: 'white', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">Upcoming Exams</Typography>
                          <EventIcon />
                        </Box>
                        <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>{dashboardStats.upcomingExams}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
              
              {/* Quick Links and Upcoming Exams Section */}
              <Grid container spacing={3}>
                {/* Quick Links */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Quick Links
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button variant="outlined" color="primary" size="small" onClick={() => handleSectionChange('teachers')}>
                        Manage Teachers
                      </Button>
                      <Button variant="outlined" color="primary" size="small" onClick={() => handleSectionChange('students')}>
                        Manage Students
                      </Button>
                      <Button variant="outlined" color="primary" size="small" onClick={() => handleSectionChange('exams')}>
                        Manage Exams
                      </Button>
                      <Button variant="outlined" color="primary" size="small" onClick={() => handleSectionChange('results')}>
                        Manage Results
                      </Button>
                      <Button variant="outlined" color="primary" size="small" startIcon={<SettingsIcon />}>
                        Global Settings
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
                
                {/* Upcoming Exams */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Upcoming Exams
                    </Typography>
                    
                    {exams.filter(exam =>
                      (exam.status === 'upcoming' || new Date(exam.date) > new Date())
                    ).slice(0, 3).map(exam => (
                      <Box key={exam._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">{exam.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(exam.date).toLocaleDateString()} {new Date(exam.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Duration: {exam.duration} minutes</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label={`${exam.students?.length || 0} Students`} size="small" color="success" variant="outlined" />
                          <Chip label="Created by" size="small" color="primary" variant="outlined" />
                          <Chip label={exam.created_by} size="small" color="info" variant="outlined" />
                        </Box>
                      </Box>
                    ))}
                    {exams.filter(exam =>
                      (exam.status === 'upcoming' || new Date(exam.date) > new Date())
                    ).length === 0 && (
                      <Typography variant="body2" color="text.secondary">No upcoming exams scheduled.</Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
          
          {/* Teachers Section */}
          {activeSection === 'teachers' && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h2">
                  Teacher Management
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                >
                  ADD TEACHER
                </Button>
              </Box>
              
              {teachers.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Username</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teachers.map((teacher) => (
                        <TableRow key={teacher._id || teacher.username}>
                          <TableCell>{teacher.username}</TableCell>
                          <TableCell>{teacher.name || '-'}</TableCell>
                          <TableCell>{teacher.email || '-'}</TableCell>
                          <TableCell>{teacher.role}</TableCell>
                          <TableCell>
                            <IconButton
                              color="error"
                              onClick={() => { setTeacherToDelete(teacher); setDeleteTeacherDialogOpen(true); }}
                              title="Delete Teacher"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body1" sx={{ mt: 2 }}>
                  No teacher accounts found. Create your first teacher account.
                </Typography>
              )}
            </Paper>
          )}
          
          {/* Students Section */}
          {activeSection === 'students' && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h2">
                  Student Management
                </Typography>
                <Typography variant="subtitle1" color="primary">
                  Total Students: {students.length}
                </Typography>
              </Box>
              
              {students.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Username</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {students.map((student) => (
                        <TableRow key={student._id || student.username}>
                          <TableCell>{student.username}</TableCell>
                          <TableCell>{student.name || '-'}</TableCell>
                          <TableCell>{student.email || '-'}</TableCell>
                          <TableCell>{student.created_by || '-'}</TableCell>
                          <TableCell>
                            <IconButton
                              color="error"
                              onClick={() => { setStudentToDelete(student); setDeleteStudentDialogOpen(true); }}
                              title="Delete Student"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body1" sx={{ mt: 2 }}>
                  No student accounts found. Teachers need to create student accounts.
                </Typography>
              )}
            </Paper>
          )}
          
          {/* Exams Section */}
          {activeSection === 'exams' && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" component="h2">
                    Exam Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sorted by latest first
                  </Typography>
                </Box>
                <Typography variant="subtitle1" color="primary">
                  Total Exams: {exams.length}
                </Typography>
              </Box>
              {/* Filter UI */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  select
                  label="Teacher"
                  value={examTeacherFilter}
                  onChange={e => setExamTeacherFilter(e.target.value)}
                  size="small"
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {Array.from(new Set(exams.map(e => e.created_by))).map(teacher => (
                    <MenuItem key={teacher} value={teacher}>{teacher}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Subject"
                  value={examSubjectFilter}
                  onChange={e => setExamSubjectFilter(e.target.value)}
                  size="small"
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {Array.from(new Set(exams.map(e => e.subject))).map(subject => (
                    <MenuItem key={subject} value={subject}>
                      {subject?.charAt(0).toUpperCase() + subject?.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Date"
                  type="date"
                  value={examDateFilter}
                  onChange={e => setExamDateFilter(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              {/* End Filter UI */}
              {exams.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Students</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Sort and filter exams */}
                      {[...exams]
                        .filter(exam =>
                          (!examTeacherFilter || exam.created_by === examTeacherFilter) &&
                          (!examSubjectFilter || exam.subject === examSubjectFilter) &&
                          (!examDateFilter || exam.date.startsWith(examDateFilter))
                        )
                        .sort((a, b) => {
                          const dateA = a.created_at ? new Date(a.created_at) :
                                       a.date ? new Date(a.date) : new Date(0);
                          const dateB = b.created_at ? new Date(b.created_at) :
                                       b.date ? new Date(b.date) : new Date(0);
                          return dateB - dateA;
                        })
                        .map((exam) => (
                          <TableRow key={exam._id}>
                            <TableCell>{exam.title}</TableCell>
                            <TableCell>
                              <Chip
                                label={exam.subject?.charAt(0).toUpperCase() + exam.subject?.slice(1) || 'Data Science'}
                                size="small"
                                color={
                                  exam.subject === 'tableau' ? 'primary' :
                                  exam.subject === 'sql' ? 'secondary' :
                                  'success'
                                }
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(exam.date).toLocaleDateString()} {new Date(exam.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </TableCell>
                            <TableCell>{exam.duration} min</TableCell>
                            <TableCell>{exam.created_by}</TableCell>
                            <TableCell>
                              <Chip
                                label={exam.status || 'upcoming'}
                                size="small"
                                color={
                                  exam.status === 'completed' ? 'success' :
                                  new Date(exam.date) <= new Date() ? 'warning' : 'info'
                                }
                              />
                            </TableCell>
                            <TableCell>{exam.students?.length || 0}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body1" sx={{ mt: 2 }}>
                  No exams found. Teachers need to create and schedule exams.
                </Typography>
              )}
            </Paper>
          )}
          
          {/* Results & Proctoring Section */}
          {activeSection === 'results' && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" component="h2">
                    Results & Proctoring
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sorted by latest submissions first
                  </Typography>
                </Box>
                <Typography variant="subtitle1" color="primary">
                  Total Submissions: {results.length}
                </Typography>
              </Box>
              
              {results.length > 0 ? (
                <Grid container spacing={3}>
                  {/* Sort results by submission date (newest first) */}
                  {[...results].sort((a, b) => {
                    // Get date values with fallbacks
                    const dateValueA = a.submitted_at || a.submission_date;
                    const dateValueB = b.submitted_at || b.submission_date;

                    // Parse dates with error handling
                    let dateA, dateB;
                    try {
                      dateA = dateValueA ? new Date(dateValueA) : new Date(0);
                      if (isNaN(dateA.getTime())) dateA = new Date(0);
                    } catch (e) {
                      console.error('Error parsing dateA:', dateValueA, e);
                      dateA = new Date(0);
                    }

                    try {
                      dateB = dateValueB ? new Date(dateValueB) : new Date(0);
                      if (isNaN(dateB.getTime())) dateB = new Date(0);
                    } catch (e) {
                      console.error('Error parsing dateB:', dateValueB, e);
                      dateB = new Date(0);
                    }

                    // Debug logging for sorting
                    console.log('Sorting comparison:', {
                      examA: a.exam_title || a.exam_id,
                      dateA: dateA.toISOString(),
                      examB: b.exam_title || b.exam_id,
                      dateB: dateB.toISOString(),
                      result: dateB.getTime() - dateA.getTime()
                    });

                    return dateB.getTime() - dateA.getTime(); // Sort descending (newest first)
                  }).map((result, index) => {
                    // Find the exam details
                    const exam = exams.find(e => e._id === result.exam_id) || {};
                    // Find the student details
                    const student = students.find(s => s._id === result.student_id || s._id === result.user_id) || {};
                    
                    // Calculate questions attempted correctly
                    const totalQuestions = result.questions ? result.questions.length :
                                         result.questions_with_answers ? result.questions_with_answers.length :
                                         result.answers ? Object.keys(result.answers).length : 0;

                    // Count only non-empty answers as attempted
                    const questionsAttempted = result.answers ?
                      Object.values(result.answers).filter(answer => answer && answer.toString().trim() !== '').length : 0;

                    const questionsRemaining = totalQuestions - questionsAttempted;
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} key={result._id || index}>
                        <Card 
                          elevation={3} 
                          sx={{ 
                            borderRadius: 2, 
                            overflow: 'hidden',
                            transition: 'transform 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 6
                            }
                          }}
                        >
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: '#2196f3', // Use a consistent blue color for all cards
                            color: 'white',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <Typography variant="h6" component="h3" sx={{ fontWeight: 'medium' }}>
                              {result.exam_title || exam.title || result.exam_id || 'Unknown Exam'}
                            </Typography>
                            <Chip
                              label={`${result.score !== undefined ? Math.round(result.score) : 0}%`}
                              color="primary"
                              sx={{
                                bgcolor: 'white',
                                color: '#2196f3',
                                fontWeight: 'bold'
                              }}
                            />
                          </Box>
                          
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Student
                              </Typography>
                              <Typography variant="body1">
                                {result.student_name || student.name || result.student_username || result.user_id || 'Unknown'}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Submitted
                              </Typography>
                              <Typography variant="body2">
                                {(() => {
                                  // Debug: Log the raw date values
                                  console.log('Result submission data:', {
                                    submission_date: result.submission_date,
                                    submitted_at: result.submitted_at,
                                    result_id: result._id
                                  });

                                  const dateValue = result.submitted_at || result.submission_date;
                                  if (!dateValue) return 'N/A';

                                  try {
                                    const date = new Date(dateValue);
                                    // Check if date is valid
                                    if (isNaN(date.getTime())) {
                                      console.error('Invalid date:', dateValue);
                                      return 'Invalid Date';
                                    }

                                    // Format with proper timezone
                                    return date.toLocaleString('en-IN', {
                                      timeZone: 'Asia/Kolkata',
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      second: '2-digit'
                                    });
                                  } catch (error) {
                                    console.error('Error formatting date:', error, dateValue);
                                    return 'Date Error';
                                  }
                                })()}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Questions
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                  size="small" 
                                  label={`${questionsAttempted} Attempted`} 
                                  color="success" 
                                  variant="outlined" 
                                />
                                {questionsRemaining > 0 && (
                                  <Chip 
                                    size="small" 
                                    label={`${questionsRemaining} Remaining`} 
                                    color="warning" 
                                    variant="outlined" 
                                  />
                                )}
                              </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                              <Button 
                                variant="outlined" 
                                size="small" 
                                color="primary"
                                onClick={() => handleViewLogs(result.exam_id, result.student_id || result.user_id)}
                                startIcon={<AssessmentIcon />}
                              >
                                View Logs
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Typography variant="body1" sx={{ mt: 2 }}>
                  No exam submissions found yet. Results will appear here after students complete exams.
                </Typography>
              )}
            </Paper>
          )}
        </Box>
      </Box>
      
      {/* Add Teacher Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add New Teacher
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="dense"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            
            <TextField
              margin="dense"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helperText="Must be at least 8 characters with uppercase, lowercase, digit, and special character (@$!%*?&)"
            />
            
            <TextField
              margin="dense"
              fullWidth
              name="name"
              label="Full Name"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            
            <TextField
              margin="dense"
              fullWidth
              name="email"
              label="Email Address"
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              helperText="Enter a valid email address (e.g., user@example.com)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            CANCEL
          </Button>
          <Button 
            onClick={handleCreateTeacher} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'CREATING...' : 'CREATE'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Proctoring Logs Dialog */}
      <Dialog
        open={viewLogsDialogOpen}
        onClose={() => setViewLogsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Proctoring Logs
          <IconButton
            aria-label="close"
            onClick={() => setViewLogsDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {proctoringLogs.length > 0 ? (
            <Box>
              {/* Create a map of exam IDs to logs */}
              {(() => {
                // Create a map of exam IDs to logs
                const examLogsMap = {};
                
                // Filter out invalid logs and group by exam ID
                proctoringLogs
                  .filter(log => {
                    // First apply exam/student filters
                    if (selectedExamId && log.exam_id !== selectedExamId) return false;
                    if (selectedStudentId && log.user_id !== selectedStudentId) return false;

                    // Filter out logs with invalid timestamps
                    if (!log.timestamp) return false;

                    try {
                      const date = new Date(log.timestamp);
                      // Check if date is valid and reasonable
                      if (isNaN(date.getTime())) return false;
                      if (date.getFullYear() < 2020 || date.getFullYear() > 2030) return false;
                    } catch (e) {
                      return false;
                    }

                    // Filter out logs with generic/invalid descriptions
                    const description = log.description || '';
                    const invalidDescriptions = [
                      'warning / alert!',
                      'warning/alert!',
                      'alert',
                      'warning',
                      ''
                    ];
                    if (invalidDescriptions.includes(description.toLowerCase().trim())) return false;

                    // Filter out logs with missing event types
                    if (!log.event_type || log.event_type.trim() === '') return false;

                    return true;
                  })
                  .forEach(log => {
                    if (!examLogsMap[log.exam_id]) {
                      examLogsMap[log.exam_id] = [];
                    }
                    examLogsMap[log.exam_id].push(log);
                  });
                
                // Sort logs within each exam by timestamp (newest first)
                Object.keys(examLogsMap).forEach(examId => {
                  examLogsMap[examId].sort((a, b) => {
                    const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
                    const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
                    return dateB - dateA; // Sort descending (newest first)
                  });
                });
                
                // Convert the map to an array of exam objects with their logs
                const examLogs = Object.keys(examLogsMap).map(examId => {
                  const exam = exams.find(e => e._id === examId) || {};

                  // Find the most recent log timestamp for this exam
                  const mostRecentTimestamp = examLogsMap[examId].reduce((latest, log) => {
                    const logTime = log.timestamp ? new Date(log.timestamp) : new Date(0);
                    return logTime > latest ? logTime : latest;
                  }, new Date(0));

                  return {
                    exam_id: examId,
                    exam_title: exam.title || 'Unknown Exam',
                    logs: examLogsMap[examId],
                    mostRecentTimestamp
                  };
                });

                // Sort exam groups by most recent activity (newest first)
                examLogs.sort((a, b) => b.mostRecentTimestamp - a.mostRecentTimestamp);
                
                return (
                  <Grid container spacing={3}>
                    {examLogs.map((examLog, examIndex) => (
                      <Grid item xs={12} key={examLog.exam_id || `exam-${examIndex}`}>
                        <Paper 
                          elevation={2} 
                          sx={{ 
                            p: 2, 
                            mb: 2, 
                            borderRadius: 2,
                            overflow: 'hidden'
                          }}
                        >
                          <Typography 
                            variant="h6" 
                            gutterBottom 
                            sx={{ 
                              p: 1, 
                              pl: 2,
                              mb: 2, 
                              borderRadius: 1,
                              bgcolor: '#2196f3',
                              color: 'white'
                            }}
                          >
                            {examLog.exam_title}
                          </Typography>
                          
                          <Grid container spacing={2}>
                            {examLog.logs.map((log, logIndex) => {
                              // Handle timestamp formatting safely
                              let formattedTime = 'Unknown';
                              try {
                                if (log.timestamp) {
                                  formattedTime = new Date(log.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
                                }
                              } catch (e) {
                                console.error('Error formatting timestamp:', e);
                              }
                              
                              // Get warning title
                              const warningTitle = getWarningTitle(log.event_type, log.description);
                              
                              return (
                                <Grid item xs={12} sm={6} md={4} key={log._id || `log-${logIndex}`}>
                                  <Card 
                                    elevation={1} 
                                    sx={{ 
                                      borderRadius: 2,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      height: '100%',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      transition: 'transform 0.2s',
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 3
                                      }
                                    }}
                                  >
                                    <CardContent sx={{ p: 2, pb: 1, flexGrow: 1 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                          {log.student_name || log.user_id || 'Unknown Student'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {formattedTime}
                                        </Typography>
                                      </Box>
                                      
                                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                        <Chip 
                                          label={log.event_type || 'Unknown'} 
                                          size="small" 
                                          color={
                                            log.event_type === 'PERSON' ? 'error' :
                                            log.event_type === 'GAZE' ? 'warning' :
                                            log.event_type === 'PHONE' ? 'error' :
                                            log.event_type === 'AUDIO' ? 'warning' :
                                            log.event_type === 'MOUTH' ? 'warning' :
                                            log.event_type === 'HEAD' ? 'warning' :
                                            log.event_type === 'SPOOF' ? 'error' :
                                            log.event_type === 'SESSION' ? 'info' :
                                            log.event_type === 'FOCUS' ? 'warning' : 'default'
                                          } 
                                        />
                                        <Chip 
                                          label={log.severity || 'Unknown'} 
                                          size="small" 
                                          color={
                                            log.severity === 'CRITICAL' ? 'error' : 
                                            log.severity === 'ALERT' ? 'error' : 
                                            log.severity === 'WARNING' ? 'warning' :
                                            log.severity === 'INFO' ? 'success' : 'default'
                                          } 
                                        />
                                      </Box>
                                      
                                      <Typography 
                                        variant="body2" 
                                        color="text.primary"
                                        sx={{ 
                                          cursor: 'pointer',
                                          '&:hover': { textDecoration: 'underline' },
                                          fontWeight: warningTitle ? 'medium' : 'normal'
                                        }}
                                        onClick={() => handleOpenWarningDialog(log)}
                                      >
                                        {warningTitle || log.description || 'No description'}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                );
              })()}
            </Box>
          ) : (
            <Typography variant="body1" sx={{ mt: 2 }}>
              No proctoring logs found for this exam or student.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewLogsDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning Dialog */}
      <Dialog
        open={warningDialogOpen}
        onClose={() => setWarningDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ErrorIcon color="error" sx={{ mr: 1 }} />
            Warning Details
          </Box>
          <IconButton onClick={() => setWarningDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedWarning && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {getWarningTitle(selectedWarning.event_type, selectedWarning.description)}
              </Typography>
              
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="body1" paragraph>
                  {selectedWarning.description}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Exam</Typography>
                    <Typography variant="body2">
                      {(() => {
                        const exam = exams.find(e => e._id === selectedWarning.exam_id);
                        return exam ? exam.title : selectedWarning.exam_id || 'Unknown';
                      })()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Student</Typography>
                    <Typography variant="body2">
                      {selectedWarning.student_name || selectedWarning.user_id || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Time</Typography>
                    <Typography variant="body2">
                      {selectedWarning.timestamp ? 
                        new Date(selectedWarning.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Severity</Typography>
                    <Chip 
                      label={selectedWarning.severity || 'Unknown'} 
                      size="small" 
                      color={
                        selectedWarning.severity === 'CRITICAL' ? 'error' : 
                        selectedWarning.severity === 'ALERT' ? 'error' : 
                        selectedWarning.severity === 'WARNING' ? 'warning' :
                        selectedWarning.severity === 'INFO' ? 'success' : 'default'
                      } 
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Teacher Confirmation Dialog */}
      <Dialog
        open={deleteTeacherDialogOpen}
        onClose={() => setDeleteTeacherDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Teacher</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete teacher <b>{teacherToDelete?.username}</b>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTeacherDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleDeleteTeacher} color="error" variant="contained" disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Student Confirmation Dialog */}
      <Dialog
        open={deleteStudentDialogOpen}
        onClose={() => setDeleteStudentDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Student</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete student <b>{studentToDelete?.username}</b>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteStudentDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleDeleteStudent} color="error" variant="contained" disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AdminDashboard;
